<template>
  <v-layout column>
    <vbt-search
      v-model="searchData"
      :items="searchFieldsConfig"
      @reset="onResetSearch"
      @search="onSearch"
    />

    <vbt-content-box
      :loading="isLoading"
      :loading-error="loadingError"
      :no-content="noContent"
      :go-back-btn="goBack"
      title="Information"
    >
      <vbt-table
        :headers="headers"
        :items="inputMessages"
        show-details-action
        @click:details="({ id }) => $router.push({
          name: 'inputMessage',
          params: { id },
          query: { suffix },
        })"
      >
        <template #id="{ item: { id } }">
          <router-link
            :to="{
              name: 'inputMessage',
              params: { id },
              query: { suffix },
            }"
            class="info--text"
          >
            {{ id }}
          </router-link>
        </template>
        <template #created="{ item: { created } }">
          {{ formatDate(created) }}
        </template>
        <template #type="{ item: { type } }">
          {{ type }}
        </template>
        <template #irSuccess="{ item: { irSuccess } }">
          <kernel-status-badge :status="irResultByValue[irSuccess]">
            {{ irSuccess }}
          </kernel-status-badge>
        </template>
        <template #orderOuterId="{ item: { orderOuterId } }">
          <router-link
            v-if="orderOuterId"
            :class="$_themeTextColorClass"
            :to="{
              name: 'order',
              params: { id: orderOuterId },
              query: { suffix },
            }"
          >
            {{ orderOuterId }}
          </router-link>
          <span v-else>
            -
          </span>
        </template>
        <template #orderItemOuterId="{ item: { orderItemOuterId } }">
          {{ orderItemOuterId || '-' }}
        </template>
      </vbt-table>

      <vbt-pagination
        v-model="pageNum"
        :items-count="pagedItemsCount"
      />
    </vbt-content-box>
  </v-layout>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';

import { useDataLoading, usePagination, useSearch } from '@mixins/factories';
import { provideThemeSettings } from '@mixins';

import { formatDate } from '@helpers';

import { namespace as EnumsNamespace } from '@store/modules/enums.types';
import { namespace as LocationsNamespace } from '@store/modules/locations.types';

import { namespace as InputMessagesNamespace, ActionTypes as InputMessagesActionTypes } from '../store.types';

const dataLoadingOptions = {
  getterName: InputMessagesActionTypes.GET_INPUT_MESSAGES,
  contentName: 'inputMessages',
};
const paginationOptions = { contentName: 'inputMessages' };
const searchOptions = {
  initialSearchDataStates: [
    'orderId', 'orderItemId', 'type', 'irSuccess',
  ],
  customTypes: {
    irSuccess: 'booleanString',
  },
};

const headers = Object.freeze([
  { text: 'ID', value: 'id' },
  { text: 'Created Date', value: 'created' },
  { text: 'Type', value: 'type' },
  { text: 'Response Status Code', value: 'responseStatusCode' },
  { text: 'IR Success', value: 'irSuccess' },
  { text: 'Order Outer ID', value: 'orderOuterId' },
  { text: 'Order Item Outer ID', value: 'orderItemOuterId' },
]);

export default {
  name: 'KernelInputMessages',
  mixins: [
    useDataLoading(dataLoadingOptions),
    usePagination(paginationOptions),
    useSearch(searchOptions),
    provideThemeSettings,
  ],
  data: () => ({ headers }),
  computed: {
    ...mapState(InputMessagesNamespace, ['inputMessages']),
    ...mapState(LocationsNamespace, ['suffix']),
    ...mapState(EnumsNamespace, {
      inputMessageTypesOptions: ({ enums: { inputMessageType: enums } }) => (enums || [])
        .map(({ value }) => value),
    }),
    ...mapGetters(EnumsNamespace, ['irResultByValue']),
    searchFieldsConfig() {
      return [
        {
          flex: 'lg3',
          value: 'orderId',
          label: 'Order ID',
          type: 'text',
        },
        {
          flex: 'lg3',
          value: 'orderItemId',
          label: 'Order Item ID',
          type: 'text',
        },
        {
          flex: 'lg3',
          value: 'type',
          label: 'Type',
          type: 'select',
          items: this.inputMessageTypesOptions,
        },
        {
          flex: 'lg3',
          value: 'irSuccess',
          label: 'IR Success',
          type: 'select',
          items: [{ value: true, text: 'Yes' }, { value: false, text: 'No' }],
        },
      ];
    },
  },
  watch: {
    suffix: '$_refreshData',
  },
  methods: {
    ...mapActions(InputMessagesNamespace, [InputMessagesActionTypes.GET_INPUT_MESSAGES]),
    formatDate,
  },
};
</script>
